/**
 * Зона воздействия землетрясения — круг вокруг эпицентра.
 *
 * Так эти карты рисовались всегда: полупрозрачный круг + красный эпицентр +
 * подпись магнитуды (`map_generator._add_earthquake_zones`, те самые картинки,
 * что уходили в телеграм). Радиус — не украшение: по нему движок правит
 * вероятность улова в точке, и приезжает он ГОТОВЫМ, с сервера
 * (`properties.impact_radius_km`). Формулу здесь не повторяем — второй источник
 * правды разъехался бы с методикой молча.
 *
 * ПОЧЕМУ ПОЛИГОН, А НЕ `circle-radius`. Круг задан в километрах, а `circle`
 * MapLibre меряет радиус в ПИКСЕЛЯХ: чтобы он означал одно и то же на всех
 * зумах, пришлось бы пересчитывать пиксели из метров при каждом движении карты,
 * да ещё по широте каждой точки. Полигон в градусах строится один раз и живёт
 * в тех же координатах, что и всё остальное на карте.
 *
 * ЗА 180-м МЕРИДИАНОМ долготу НЕ заворачиваем: полигон, у которого точки
 * перепрыгивают с 179,8 на −179,8, MapLibre растянет через весь мир полосой.
 * Непрерывный выход за 180 он рисует правильно.
 */

/** Километров в градусе широты. Меридиан почти ровный, одной цифры довольно. */
const KM_PER_DEG_LAT = 111.32

/** Точек на окружность. 64 — на глаз уже гладко, а вершин втрое меньше, чем у 180. */
const STEPS = 64

export interface QuakeProps {
  id?: string
  magnitude: number
  impact_radius_km?: number | null
  [key: string]: unknown
}

/**
 * Кольцо круга радиусом `radiusKm` вокруг точки, в градусах.
 *
 * Градус долготы короче градуса широты в cos(широты) раз — на 60° вдвое.
 * Круг, построенный одним радиусом в градусах, был бы эллипсом, вытянутым
 * поперёк, и обещал бы влияние там, где его нет (та же ошибка, что чинилась
 * в `src/geo/distance.py`).
 */
export function impactRing(
  lon: number,
  lat: number,
  radiusKm: number,
  steps: number = STEPS,
): [number, number][] {
  const dLat = radiusKm / KM_PER_DEG_LAT
  // У полюса cos → 0 и круг раздувается на весь мир. Землетрясений там не
  // бывает, но делить на ноль всё равно нельзя.
  const cos = Math.max(Math.cos((lat * Math.PI) / 180), 1e-6)
  const dLon = dLat / cos
  const ring: [number, number][] = []
  for (let i = 0; i < steps; i++) {
    const a = (2 * Math.PI * i) / steps
    ring.push([lon + dLon * Math.cos(a), lat + dLat * Math.sin(a)])
  }
  ring.push(ring[0]) // GeoJSON требует замкнутого кольца
  return ring
}

/**
 * Точки землетрясений → полигоны зон воздействия (свойства переносятся как есть).
 *
 * Событие без радиуса пропускаем, а не подставляем свой: «радиуса не приехало»
 * и «зона нулевая» — разные вещи, и рисовать вторую вместо первой значит
 * обещать точность, которой нет.
 */
export function earthquakeZones(
  fc: GeoJSON.FeatureCollection | null | undefined,
): GeoJSON.FeatureCollection {
  const out: GeoJSON.Feature[] = []
  for (const f of fc?.features ?? []) {
    if (f.geometry?.type !== 'Point') continue
    const props = (f.properties ?? {}) as QuakeProps
    const radius = Number(props.impact_radius_km)
    if (!Number.isFinite(radius) || radius <= 0) continue
    const [lon, lat] = f.geometry.coordinates as [number, number]
    out.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [impactRing(lon, lat, radius)] },
      properties: { ...props },
    })
  }
  return { type: 'FeatureCollection', features: out }
}

/**
 * Насколько густо заливать зону. Слабое событие не должно закрашивать карту
 * так же, как сильное: на картинках телеграма плотность падала от события к
 * событию (0.15 / 0.12 / 0.09), но там их было ровно три и порядок задавала
 * значимость. В кадре кабинета событий бывают десятки, и порядок им не назначен
 * — поэтому густота берётся от магнитуды, а не от места в списке.
 */
export function zoneOpacity(magnitude: number): number {
  if (!Number.isFinite(magnitude)) return 0.08
  if (magnitude >= 6.5) return 0.2
  if (magnitude >= 5.0) return 0.14
  return 0.08
}

/**
 * Радиус кружка эпицентра в пикселях. Растёт с магнитудой, но медленно: это
 * метка события, а размер влияния показывает зона.
 */
export function epicenterRadius(magnitude: number): number {
  if (!Number.isFinite(magnitude)) return 4
  return Math.max(4, Math.min(10, 4 + (magnitude - 4) * 1.5))
}
